<template>
  <v-container>
    <!-- Título e Botões -->
    <v-row class="align-center mb-5">
      <v-col cols="auto">
        <h3 class="page-title">
          <v-icon color="primary" class="mr-2">mdi-test-tube</v-icon>
          Exames
        </h3>
      </v-col>

      <v-spacer></v-spacer>

      <v-col cols="auto">
        <!-- Botão para reabrir o tour desta aba -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              aria-label="Ver o tour desta aba"
              class="mr-3"
              @click="startTour"
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver o tour desta aba</span>
        </v-tooltip>

        <!-- Botão para atualizar a lista -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              :loading="loading"
              class="mr-3"
              @click="loadExams"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Atualizar lista</span>
        </v-tooltip>

        <!-- Botão para adicionar exame -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="openExamAddDialog"
              class="mr-3"
              color="primary"
              data-tour="ex-add"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Solicitar novo exame</span>
        </v-tooltip>

        <!-- Botão para buscar -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="searchExams"
              class="mr-3"
            >
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>Buscar exames</span>
        </v-tooltip>

        <!-- Botão de ordenação -->
        <v-menu v-model="menuOpen" :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="{ ...menuAttrs, ...tipAttrs }"
                  v-on="{ ...menuOn, ...tipOn }"
                >
                  <v-icon>mdi-sort</v-icon>
                </v-btn>
              </template>
              <span>Ordenar por data</span>
            </v-tooltip>
          </template>

          <v-list>
            <v-list-item @click="sortExams('asc')">
              <v-list-item-title>Crescente</v-list-item-title>
            </v-list-item>
            <v-list-item @click="sortExams('desc')">
              <v-list-item-title>Decrescente</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Tabela -->
    <v-data-table
      :headers="headers"
      :items="exams"
      item-key="id"
      class="elevation-1"
      data-tour="ex-list"
      :loading="loading"
      loading-text="Carregando..."
      :no-data-text="'Nenhum registro encontrado'"
      :footer-props="{
        itemsPerPageText: 'Itens por página:',
        pageText: '{0}-{1} de {2}',
      }"
    >
      <template v-slot:item="{ item, index }">
        <tr>
          <td>{{ formatDate(item.created_at) }}</td>
          <td>{{ formatDate(item.updated_at) }}</td>
          <td>{{ formatDate(item.date) }}</td>
          <td>{{ item.exam_name }}</td>
          <td>{{ item.lab_name || "N/A" }}</td>
          <td>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  small
                  text-color="white"
                  v-bind="attrs"
                  v-on="on"
                  :data-tour="index === 0 ? 'ex-status' : null"
                >
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>
              <span>{{ getStatusTooltip(item.status) }}</span>
            </v-tooltip>
          </td>
          <td>{{ item.observations || "N/A" }}</td>
          <td
            class="text-center actions-cell"
            :data-tour="index === 0 ? 'ex-actions' : null"
          >
            <!-- Botão para completar exame pendente -->
            <v-tooltip bottom v-if="item.status === 'pending'">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="success"
                  v-bind="attrs"
                  v-on="on"
                  @click="openDialogToComplete(item)"
                  class="mr-1"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Anexar resultados do exame</span>
            </v-tooltip>

            <!-- Botão para visualizar resultados -->
            <v-tooltip bottom v-if="item.status === 'completed'">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small color="info" v-bind="attrs" v-on="on" @click="viewResults(item)" class="mr-1">
                  <v-icon small>mdi-file-document</v-icon>
                </v-btn>
              </template>
              <span>Visualizar resultados</span>
            </v-tooltip>

            <!-- Botão de excluir -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="confirmDelete(item)"
                  color="error"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Excluir este exame</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Modal de Prescrição -->
    <ExamAddDialog v-model="showExamAddDialog" @saved="loadExams" />

    <!-- Modal para Completar Exame -->
    <v-dialog
      v-model="dialogUploadExams"
      max-width="1200px"
      transition="dialog-bottom-transition"
      persistent
      scrollable
    >
      <v-card class="ds-dialog">
        <!-- Overlay de loading -->
        <v-overlay :value="isUploading" absolute z-index="9999">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              size="64"
              color="primary"
            ></v-progress-circular>
            <div class="mt-4 white--text">Processando exame, aguarde...</div>
          </div>
        </v-overlay>

        <!-- Cabeçalho do Modal -->
        <v-card-title class="ds-dialog-header py-4">
          <span class="ds-dialog-title">Completar Exame: {{ currentExam?.exam_name }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                class="close-btn"
                color="white"
                v-bind="attrs"
                v-on="on"
                @click="closeDialog"
                :disabled="isUploading || isSaving"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Fechar</span>
          </v-tooltip>
        </v-card-title>

        <!-- Área de upload e visualização -->
        <v-card-text class="ds-dialog-body">
          <v-row no-gutters>
            <!-- Lado esquerdo: PDF Preview -->
            <v-col cols="7" class="pr-4">
              <section class="ds-form-section ds-form-section--flush">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-file-pdf-box</v-icon>
                    <span>Visualização do Documento</span>
                  </div>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="triggerFileInput"
                        :disabled="isUploading || isSaving"
                        class="ml-2"
                        small
                        depressed
                      >
                        <v-icon left>mdi-upload</v-icon>
                        Selecionar documento
                      </v-btn>
                    </template>
                    <span
                      >Escolha o PDF do laudo para extrair os dados
                      automaticamente</span
                    >
                  </v-tooltip>
                  <input
                    type="file"
                    ref="fileInput"
                    accept=".pdf"
                    style="display: none"
                    @change="onFileSelected"
                  />
                </div>

                <!-- Altura fixa da prévia: o iframe do PDF precisa dela. -->
                <div class="exam-pdf-preview">
                  <!-- Placeholder quando não há PDF -->
                  <div
                    v-if="!pdfUrl"
                    class="fill-height d-flex align-center justify-center grey lighten-3"
                  >
                    <div class="text-center">
                      <v-icon size="64" color="grey darken-1"
                        >mdi-file-pdf-box</v-icon
                      >
                      <div class="mt-2 grey--text text--darken-1">
                        Nenhum documento selecionado
                      </div>
                    </div>
                  </div>

                  <!-- PDF Preview -->
                  <iframe
                    v-if="pdfUrl"
                    :src="pdfUrl"
                    class="fill-height"
                    style="width: 100%; border: none"
                  ></iframe>
                </div>
              </section>
            </v-col>

            <!-- Lado direito: Formulário -->
            <v-col cols="5">
              <!-- Formulário -->
              <v-form ref="form" v-model="formValid">
                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-information</v-icon>
                      <span>Informações do Exame</span>
                    </div>
                  </div>

                  <!-- Alerta de erro -->
                  <v-alert
                    v-if="errorMessage"
                    type="error"
                    text
                    dense
                    dismissible
                    @input="errorMessage = null"
                    class="mb-3"
                  >
                    {{ errorMessage }}
                  </v-alert>

                  <!-- Nome do Paciente (extraído) -->
                  <v-text-field
                    v-model="examForm.patient_name"
                    label="Nome do Paciente"
                    prepend-inner-icon="mdi-account"
                    clearable
                    outlined
                    dense
                    color="primary"
                    hide-details="auto"
                    :disabled="isUploading || isSaving"
                    readonly
                  ></v-text-field>

                  <v-row dense class="mt-2">
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="examForm.date"
                        label="Data do Exame"
                        type="date"
                        prepend-inner-icon="mdi-calendar"
                        clearable
                        outlined
                        dense
                        color="primary"
                        hide-details="auto"
                        :disabled="isUploading || isSaving"
                      />
                    </v-col>

                    <!-- Laboratório -->
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="examForm.lab_name"
                        label="Laboratório"
                        prepend-inner-icon="mdi-hospital-building"
                        clearable
                        outlined
                        dense
                        color="primary"
                        hide-details="auto"
                        :disabled="isUploading || isSaving"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Observações -->
                  <v-textarea
                    v-model="examForm.observations"
                    label="Observações"
                    prepend-inner-icon="mdi-text"
                    clearable
                    outlined
                    dense
                    color="primary"
                    rows="2"
                    auto-grow
                    hide-details="auto"
                    class="mt-3"
                    :disabled="isUploading || isSaving"
                  ></v-textarea>
                </section>

                <!-- Retângulos com dados dos parâmetros -->
                <section v-if="examForm.parametros?.length" class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                      <span>Resultados Extraídos</span>
                    </div>
                  <span class="ds-form-section__aside">
                    {{ examForm.parametros.length }} parâmetros
                  </span>
                  </div>
                  <v-row dense style="max-height: 300px; overflow-y: auto">
                    <v-col
                      cols="12"
                      v-for="(param, index) in examForm.parametros"
                      :key="index"
                    >
                      <v-card
                        outlined
                        class="pa-3 rounded-lg"
                        style="border: 1.5px solid #000 !important"
                      >
                        <div class="text-body-2">
                          <div class="font-weight-bold mb-1">
                            {{ param.tipo_analise }}
                          </div>
                          <div>
                            <strong>Resultado:</strong>
                            {{ param.resultado }} {{ param.unidade }}
                          </div>
                          <div>
                            <strong>Referência:</strong>
                            {{ param.referencia }}
                          </div>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </section>
              </v-form>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Rodapé do Modal -->
        <v-card-actions class="ds-dialog-actions">
          <v-btn
            outlined
            color="primary"
            @click="closeDialog"
            :disabled="isUploading || isSaving"
          >
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :disabled="!pdfUrl || isUploading"
            :loading="isSaving"
            @click="completeExam"
          >
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para visualizar resultados -->
    <v-dialog v-model="dialogViewResults" scrollable max-width="800px">
      <v-card class="ds-dialog" v-if="selectedExam">
        <v-card-title class="ds-dialog-header py-4">
          <span class="ds-dialog-title">Resultados - {{ selectedExam.exam_name }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="white"
                class="close-btn"
                v-bind="attrs"
                v-on="on"
                @click="dialogViewResults = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Fechar</span>
          </v-tooltip>
        </v-card-title>
        <v-card-text class="ds-dialog-body">
          <div v-if="selectedExam.results">
            <section class="ds-form-section ds-form-section--flush">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-account-circle</v-icon>
                  <span>Informações do Paciente</span>
                </div>
              </div>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-medium">Paciente:</td>
                    <td>{{ selectedExam.results.cabecalho?.nome_paciente }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Data de Nascimento:</td>
                    <td>
                      {{ selectedExam.results.cabecalho?.data_nascimento }}
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Data do Exame:</td>
                    <td>{{ selectedExam.results.cabecalho?.data_exame }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Laboratório:</td>
                    <td>{{ selectedExam.results.cabecalho?.laboratorio }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </section>

            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-flask-outline</v-icon>
                  <span>Parâmetros Analisados</span>
                </div>
                <span
                  v-if="selectedExam.results.parametros"
                  class="ds-form-section__aside"
                >{{ selectedExam.results.parametros.length }}</span>
              </div>
              <v-row dense>
                <v-col
                  cols="12"
                  v-for="(param, index) in selectedExam.results.parametros"
                  :key="index"
                >
                  <v-card outlined class="pa-3">
                    <div class="font-weight-bold mb-2">
                      {{ param.tipo_analise }}
                    </div>
                    <div class="text-body-2">
                      <div>
                        <strong>Resultado:</strong> {{ param.resultado }}
                        {{ param.unidade }}
                      </div>
                      <div>
                        <strong>Referência:</strong> {{ param.referencia }}
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </section>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Dialog de confirmação de exclusão -->
  </v-container>
</template>

<script>
import http from "@/services/http";
import { initEcho, refreshEchoAuthToken } from "@/plugins/echo";
import { useSnackbar } from "@/composables/useSnackbar";
import ExamAddDialog from "@/features/patients/exams/ExamAddDialog.vue";
import tourMixin from "@/features/tour/tourMixin";
import { formatDate } from "@/utils/datetime";

export default {
  mixins: [tourMixin],

  components: {
    ExamAddDialog,
  },

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  data() {
    return {
      loading: false,
      dialogUploadExams: false,
      dialogViewResults: false,
      isDeleting: false,
      examToDelete: null,
      menuOpen: false,
      isUploading: false,
      isSaving: false,
      pdfUrl: null,
      // Fase 3: token do upload assincrono em andamento (null quando sincrono/ocioso).
      pendingExamToken: null,
      formValid: false,
      errorMessage: null,
      selectedExam: null,
      currentExam: null,
      showExamAddDialog: false,

      headers: [
        { text: "Solicitação",  value: "created_at" },
        { text: "Envio",        value: "updated_at" },
        { text: "Realização",   value: "date" },
        { text: "Exame",        value: "exam_name" },
        { text: "Laboratório",  value: "lab_name" },
        { text: "Status",       value: "status" },
        { text: "Observações",  value: "observations" },
        { text: "Ações",        value: "actions", sortable: false, align: "center" },
      ],

      exams: [],

      examForm: {
        patient_name: null,
        date: null,
        lab_name: null,
        observations: null,
        parametros: [],
        results_json: null,
      },
    };
  },

  mounted() {
    this.loadExams();
  },

  computed: {
    tourId() {
      return "sec-exames-pg";
    },
    currentUserId() {
      return this.$store.getters["auth/userId"];
    },
  },

  beforeDestroy() {
    this.teardownExamListener();
  },

  methods: {
    formatDate: (v) => formatDate(v, { fallback: "N/A" }),
    tourReady() {
      return !this.loading;
    },

    tourSteps() {
      return [
        {
          title: "Exames do paciente",
          description:
            "Aqui você solicita exames e acompanha os resultados de cada um. Vou te guiar pelos pontos principais.",
        },
        {
          anchor: "ex-add",
          title: "Solicitar novo exame",
          description:
            "Clique aqui para pedir um novo exame ao paciente — ele entra na lista como pendente até os resultados chegarem.",
        },
        {
          anchor: "ex-list",
          title: "Lista de exames",
          description:
            "Cada linha traz um exame com suas datas, laboratório e observações. Um clique nas ações abre os detalhes.",
        },
        {
          anchor: "ex-status",
          title: "Status do exame",
          description:
            "A cor indica em que ponto o exame está: pendente, em progresso ou concluído com laudo anexado.",
        },
        {
          anchor: "ex-actions",
          title: "Ações do exame",
          description:
            "Aqui você anexa os resultados de um exame pendente, visualiza o laudo já enviado ou remove o registro.",
        },
      ];
    },

    async loadExams() {
      this.loading = true;
      try {
        const patientId = this.$route.params.id;

        if (!patientId) {
          this.error("ID do paciente não encontrado na URL.");
          return;
        }

        const response = await http.get(
          `/api/exams/get-all-exams/${patientId}`,
        );
        const list = response.data.data || response.data || [];

        // Ordena pelo mais recentemente criado no topo.
        // O backend ordena por `date`, mas exames pendentes (vindos de
        // prescrição) têm date=null e iriam pro fim — a ordem por
        // created_at garante que o último solicitado aparece primeiro.
        this.exams = [...list].sort((a, b) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          return dateB - dateA;
        });

        if (this.exams.length === 0) {
          this.warning("Sem registro de exames para este paciente!");
        } else {
          this.success(
            `${this.exams.length} ${
              this.exams.length === 1 ? "exame carregado" : "exames carregados"
            } com sucesso`,
          );
        }
      } catch (err) {
        console.error("Erro ao carregar exames:", err);
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Erro ao carregar a lista de exames.";
        this.erroDeRequisicao(err, msg);
        this.exams = [];
      } finally {
        this.loading = false;
      }
    },

    openDialogToComplete(exam) {
      this.currentExam = exam;
      this.resetForm();
      this.dialogUploadExams = true;
    },

    openExamAddDialog() {
      this.showExamAddDialog = true;
    },

    closeDialog() {
      if (this.isUploading || this.isSaving) return;
      this.dialogUploadExams = false;
      this.currentExam = null;
      this.resetForm();
    },

    resetForm() {
      this.examForm = {
        patient_name: null,
        date: null,
        lab_name: null,
        observations: null,
        parametros: [],
        results_json: null,
      };
      this.pdfUrl = null;
      this.errorMessage = null;

      if (this.$refs.form) this.$refs.form.resetValidation();
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.type !== "application/pdf") {
        this.errorMessage = "Por favor, selecione um arquivo PDF válido.";
        event.target.value = "";
        return;
      }

      this.isUploading = true;
      this.errorMessage = null;

      // Preview do PDF
      const reader = new FileReader();
      reader.onload = (e) => {
        this.pdfUrl = e.target.result;
      };
      reader.readAsDataURL(file);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("examType", "extractor_sangue");

        const response = await http.post(`/api/process-exam-pdf`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // Fase 3: extração assíncrona. O backend devolve 202 + token e o
        // resultado chega depois pelo websocket (evento ExamProcessed).
        if (response.status === 202 && response.data?.async) {
          this.pendingExamToken = response.data.token;
          const armed = this.listenExamProcessed();
          if (!armed) {
            // Sem websocket: listenExamProcessed já avisou e liberou o spinner.
            // Mantém o modal aberto pra mostrar o erro e permitir novo envio.
            return;
          }
          // UX assíncrona de verdade: NÃO prende o médico na tela enquanto o
          // laudo processa (~100s+). Fecha o modal e libera a navegação; o
          // listener vive na PÁGINA (não no modal), então segue ouvindo e
          // reabre o form preenchido quando o ExamProcessed chegar. Preserva
          // currentExam/pdfUrl/token de propósito — nada de resetForm aqui.
          this.isUploading = false;
          this.dialogUploadExams = false;
          this.info(
            "Processando o laudo em segundo plano. Pode continuar trabalhando — avisaremos quando terminar.",
            6000,
          );
          return;
        }

        // Caminho síncrono (flag EXAM_ASYNC off): resultado já veio no corpo.
        this.applyExtractedData(response.data);
        this.success("PDF processado com sucesso!");
      } catch (err) {
        console.error("Erro ao processar o PDF:", err);

        if (err.response) {
          this.errorMessage =
            err.response.data?.message ||
            err.response.data?.error ||
            "Erro ao processar o PDF. Verifique o arquivo e tente novamente.";
        } else {
          this.errorMessage =
            "Erro de conexão. Verifique sua internet e tente novamente.";
        }

        this.pdfUrl = null;
        event.target.value = "";
      } finally {
        // No modo assíncrono o modal já foi fechado e isUploading zerado no
        // ramo do 202 (pendingExamToken segue setado). Aqui só cobre os demais
        // caminhos (síncrono/erro), sem reativar o spinner do background.
        if (!this.pendingExamToken) {
          this.isUploading = false;
        }
      }
    },

    /** Preenche o form do exame a partir do JSON extraído (síncrono ou via websocket). */
    applyExtractedData(data) {
      this.examForm.patient_name =
        data?.cabecalho?.nome_paciente || "Não identificado";
      this.examForm.date = this.convertDateToISO(
        data?.cabecalho?.data_exame || null,
      );
      this.examForm.lab_name =
        data?.cabecalho?.laboratorio !== "none"
          ? data?.cabecalho?.laboratorio
          : "";
      this.examForm.parametros = Array.isArray(data?.parametros)
        ? data.parametros
        : [];
      this.examForm.results_json = data;
    },

    /**
     * Fase 3: escuta ExamProcessed no canal privado do próprio usuário (o mesmo
     * das notificações). Só reage ao upload que ESTE modal disparou (casa o token).
     *
     * @return {boolean} true se o listener foi (ou já estava) armado; false se
     *   não há websocket disponível — nesse caso o chamador mantém o modal aberto
     *   pra mostrar o erro em vez de fingir que processa em segundo plano.
     */
    listenExamProcessed() {
      if (this.examChannelRef) return true; // já armado por um upload anterior
      try {
        refreshEchoAuthToken();
        const echo = initEcho();
        if (!echo || !this.currentUserId) {
          // Sem websocket não há como receber o resultado assíncrono — libera a UI.
          this.isUploading = false;
          this.pendingExamToken = null;
          this.errorMessage =
            "Não foi possível abrir a conexão em tempo real. Recarregue e tente novamente.";
          return false;
        }

        this.examChannelRef = echo.private(
          `App.Models.User.${this.currentUserId}`,
        );
        this.examChannelRef.listen(".ExamProcessed", (payload) => {
          // Ignora eventos de outros uploads.
          if (!payload || payload.token !== this.pendingExamToken) return;

          this.clearExamTimeout();
          this.pendingExamToken = null;
          this.isUploading = false;

          if (payload.status === "completed") {
            this.applyExtractedData(payload.result);
            // O modal foi fechado ao mandar pro background: reabre já
            // preenchido pro médico conferir e salvar (currentExam preservado).
            this.dialogUploadExams = true;
            this.success("Laudo processado. Confira os dados e salve.", 6000);
          } else {
            // Modal fechado: o erro precisa vir por toast, não pelo errorMessage
            // interno (que ninguém veria).
            this.pdfUrl = null;
            this.error(
              payload.error ||
                "Não foi possível processar o laudo. Tente novamente.",
              6000,
            );
          }
        });

        // Salvaguarda: se o resultado NÃO chegar pelo websocket, não deixa o
        // spinner girar pra sempre — para, avisa e libera o retry.
        //
        // O prazo precisa ficar ACIMA do orçamento do backend, senão o front
        // desiste enquanto a extração ainda roda: a chamada externa tem 180s
        // (ExamPdfExtractor) e o Job da fila 200s (ProcessExamPdfJob). Um laudo
        // de sangue extrai 1 chamada de LLM POR PÁGINA (sequencial), então
        // hemogramas de várias páginas passam de 90s com facilidade. Com o timer
        // curto, o token era zerado antes do fim e o ExamProcessed que chegava
        // depois era ignorado (token != pendingExamToken) — o dado extraído se
        // perdia. 210s cobre uma tentativa completa do backend + folga de rede.
        this.clearExamTimeout();
        this.examProcessTimer = setTimeout(() => {
          if (!this.pendingExamToken) return; // já resolveu
          this.pendingExamToken = null;
          this.isUploading = false;
          this.pdfUrl = null;
          // Modal já fechado (processa em segundo plano): avisa por toast.
          this.error(
            "O processamento demorou mais que o esperado. Tente novamente.",
            6000,
          );
        }, 210000);
        return true;
      } catch (e) {
        console.warn("[exams] listener ExamProcessed falhou (ignorado):", e);
        this.isUploading = false;
        this.pendingExamToken = null;
        return false;
      }
    },

    /** Cancela o timeout de segurança do processamento assíncrono, se armado. */
    clearExamTimeout() {
      if (this.examProcessTimer) {
        clearTimeout(this.examProcessTimer);
        this.examProcessTimer = null;
      }
    },

    /** Remove SÓ o listener de exame; não faz leave (as notificações usam o mesmo canal). */
    teardownExamListener() {
      this.clearExamTimeout();
      try {
        if (this.examChannelRef) {
          this.examChannelRef.stopListening(".ExamProcessed");
          this.examChannelRef = null;
        }
      } catch (e) {
        console.warn("[exams] teardown ExamProcessed falhou (ignorado):", e);
      }
    },

    convertDateToISO(dateString) {
      if (!dateString) return null;
      const parts = dateString.split("/");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      return dateString;
    },

    async completeExam() {
      if (!this.currentExam) {
        this.error("Nenhum exame selecionado.");
        return;
      }

      this.isSaving = true;
      this.errorMessage = null;

      try {
        const payload = {
          date: this.examForm.date,
          lab_name: this.examForm.lab_name || "",
          observations: this.examForm.observations || "",
          results: JSON.stringify(this.examForm.results_json),
          status: "completed",
        };

        await http.put(`/api/exams/update/${this.currentExam.id}`, payload, {
          headers: { "Content-Type": "application/json" },
        });

        this.success("Exame completado com sucesso!");

        this.dialogUploadExams = false;
        this.currentExam = null;
        this.resetForm();

        await this.loadExams();
      } catch (err) {
        console.error("Erro ao completar exame:", err);

        if (err.response) {
          this.errorMessage =
            err.response.data?.message ||
            err.response.data?.error ||
            "Erro ao completar o exame.";
        } else {
          this.errorMessage = "Erro de conexão. Tente novamente.";
        }
      } finally {
        this.isSaving = false;
      }
    },

    searchExams() {
      this.info("Funcionalidade de busca a ser implementada.");
    },

    sortExams(direction) {
      this.exams.sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return direction === "asc" ? dateA - dateB : dateB - dateA;
      });
      this.menuOpen = false;
    },

    getStatusColor(status) {
      const colors = {
        pending: "orange",
        completed: "green",
        in_progress: "blue",
      };
      return colors[status] || "blue-grey";
    },

    getStatusText(status) {
      const texts = {
        pending: "Pendente",
        completed: "Completo",
        in_progress: "Em Progresso",
      };
      return texts[status] || status;
    },

    getStatusTooltip(status) {
      const tooltips = {
        pending: "Aguardando o paciente anexar os resultados",
        completed: "Exame finalizado com resultados anexados",
        in_progress: "Exame em andamento",
      };
      return tooltips[status] || "Status não identificado";
    },

    async confirmDelete(exam) {
      this.examToDelete = exam;
      let message = `Tem certeza que deseja excluir o exame "${exam?.exam_name || ""}"?`;
      if (exam?.prescription_item_id) {
        message +=
          "\nEste exame está vinculado a uma prescrição — a exclusão também removerá o item correspondente (e a prescrição inteira se for o único item).";
      }
      const ok = await this.$confirm({
        type: "delete",
        title: "Excluir exame",
        message,
        hint: "Esta ação pode ser desfeita posteriormente (exclusão lógica).",
      });
      if (!ok) {
        this.examToDelete = null;
        return;
      }
      await this.deleteExam();
    },

    async deleteExam() {
      if (!this.examToDelete) return;
      this.isDeleting = true;
      try {
        await http.delete(`/api/exams/destroy/${this.examToDelete.id}`);
        this.success("Exame excluído com sucesso!");
        this.examToDelete = null;
        await this.loadExams();
      } catch (err) {
        console.error("Erro ao excluir exame:", err);
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Erro ao excluir exame. Tente novamente.";
        this.erroDeRequisicao(err, msg);
      } finally {
        this.isDeleting = false;
      }
    },

    viewResults(item) {
      this.selectedExam = {
        ...item,
        results:
          typeof item.results === "string"
            ? JSON.parse(item.results)
            : item.results,
      };
      this.dialogViewResults = true;
    },
  },
};
</script>

<style scoped>
/* Prévia do PDF no diálogo "Completar Exame": altura fixa só da prévia (o
   iframe precisa dela); o corpo do diálogo não tem altura própria. */
.exam-pdf-preview {
  height: 540px;
}

.position-relative {
  position: relative;
}

/* ═══ Page title ═══ */
.page-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.3px;
  margin: 0;
}

/* ═══ Tabelas padronizadas (estilo BodyCompositionDialog) ═══ */
.v-data-table >>> thead th,
.v-simple-table >>> thead th {
  background-color: var(--ds-blue-50) !important;
  border-bottom: 2px solid var(--ds-blue-100) !important;
  padding: 12px !important;
  font-size: 10px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.4px !important;
  color: var(--ds-primary) !important;
  font-weight: 700 !important;
  height: auto !important;
}

.v-data-table >>> tbody tr,
.v-simple-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.v-data-table >>> tbody tr:hover,
.v-simple-table >>> tbody tr:hover {
  background-color: var(--ds-blue-50) !important;
}

.v-data-table >>> tbody td,
.v-simple-table >>> tbody td {
  border-bottom: 1px solid var(--ds-blue-divider) !important;
  font-size: 12px !important;
  color: var(--ds-text-600) !important;
}

/* ═══ Célula de ações ═══ */
.actions-cell {
  white-space: nowrap;
  min-width: 120px;
}

/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
</style>
